/**
 * 质感风格库 + 主体"填入框架"模板（P-C1，对齐竞品主体创作流程）。
 *
 * 风格：一组预设视觉风格，选中后其 prompt 后缀追加到主体生成提示词，统一出片调性。
 * 框架：按类别给一段结构化填空模板，帮新手把"身份/体型/服装…"一次想全（竞品"填入框架"）。
 * 纯数据 + 纯函数，可测；风格数量后续可扩（接真后端时并入模型侧风格表）。
 */
import type { SubjectCategory } from './subject';

export interface StylePreset {
  id: string;
  name: string;
  group: '真人' | '动漫' | '三维' | '风格化';
  /** 追加到正向提示词的风格后缀 */
  suffix: string;
}

/** 不选风格（保持原样）。 */
export const STYLE_NONE_ID = '';

/** 质感风格预设（近 30 种，取自公开手册风格梳理，命名我方自定、后缀我方自写）。 */
export const STYLE_PRESETS: StylePreset[] = [
  // 真人
  { id: 'real-pro', name: '真人写实Pro', group: '真人', suffix: '真人实拍质感, 电影级皮肤细节, 8K, 浅景深' },
  { id: 'guofeng-pro', name: '真人古装Pro', group: '真人', suffix: '真人古装, 考究服化道, 电影质感, 柔和布光' },
  { id: 'xianxia-pro', name: '真人仙侠Pro', group: '真人', suffix: '仙侠写实, 飘逸衣袂, 仙气氛围, 电影质感' },
  { id: 'real', name: '真人写实', group: '真人', suffix: '真实摄影质感, 自然光, 高清细节' },
  { id: 'guofeng', name: '真人古装', group: '真人', suffix: '古装造型, 中式服化道' },
  { id: 'xianxia', name: '真人仙侠', group: '真人', suffix: '仙侠造型, 飘逸出尘' },
  { id: 'retro', name: '真人年代感', group: '真人', suffix: '复古胶片色调, 年代质感' },
  { id: 'wasteland', name: '真人废土', group: '真人', suffix: '废土风, 粗粝质感, 低饱和, 尘埃氛围' },
  // 动漫
  { id: 'anime-cn2d', name: '2D国风动漫', group: '动漫', suffix: '2D 国风动画, 工笔线条, 东方配色' },
  { id: 'anime-romance', name: '动漫古言', group: '动漫', suffix: '古言动漫风, 唯美人物, 柔和上色' },
  { id: 'anime-apoc', name: '动漫末世', group: '动漫', suffix: '末世动漫风, 冷色调, 硬朗线条' },
  { id: 'anime-jp', name: '日系漫画', group: '动漫', suffix: '日系赛璐璐动画, 干净线条, 明亮配色' },
  { id: 'anime-new', name: '新时代动漫', group: '动漫', suffix: '现代动漫风, 精致作画, 电影级光影' },
  { id: 'ink-wuxia', name: '水墨武侠', group: '动漫', suffix: '中国水墨风, 写意留白, 墨色浓淡' },
  { id: 'manga-real', name: '写实漫画', group: '动漫', suffix: '写实漫画风, 细腻排线, 强明暗' },
  { id: 'cinematic-manga', name: '电影写实动漫', group: '动漫', suffix: '电影感动漫, 厚涂质感, 戏剧光影' },
  // 三维
  { id: 'cn3d', name: '3D国风动漫', group: '三维', suffix: '3D 国风渲染, 精致材质, 柔和全局光' },
  { id: 'cartoon3d', name: '3D卡通', group: '三维', suffix: '3D 卡通渲染, 圆润造型, 明快配色' },
  { id: 'realistic3d', name: '逼真3D渲染', group: '三维', suffix: '照片级 3D 渲染, PBR 材质, 全局光照' },
  { id: 'chibi3d', name: '3D Q版', group: '三维', suffix: '3D Q版, 大头身, 软萌质感' },
  { id: 'chibi2d', name: '2D Q版', group: '三维', suffix: '2D Q版, 简笔萌系, 平涂上色' },
  { id: 'fairy2d', name: '童话2D', group: '三维', suffix: '童话绘本风, 温暖笔触, 梦幻配色' },
  // 风格化
  { id: 'scifi', name: '科幻写实', group: '风格化', suffix: '硬科幻质感, 冷色科技光, 精密细节' },
  { id: 'cyberpunk', name: '赛博朋克', group: '风格化', suffix: '赛博朋克, 霓虹光, 高对比, 雨夜都市' },
  { id: 'ghibli', name: '吉卜力', group: '风格化', suffix: '吉卜力手绘风, 温暖自然, 细腻背景' },
  { id: 'superhero', name: '美式超级英雄', group: '风格化', suffix: '美漫英雄风, 强肌肉线条, 戏剧构图' },
  { id: 'panda', name: '熊猫头风格', group: '风格化', suffix: '熊猫头表情包风, 简笔搞笑' },
];

export function getStyle(id: string): StylePreset | undefined {
  return STYLE_PRESETS.find((s) => s.id === id);
}

/** 风格后缀（无风格返回空串，供 subjectPrompt 追加）。 */
export function styleSuffix(id: string | undefined): string {
  if (id === undefined || id === STYLE_NONE_ID) {
    return '';
  }
  return getStyle(id)?.suffix ?? '';
}

/** 分组顺序（下拉/选择器用）。 */
export const STYLE_GROUPS: StylePreset['group'][] = ['真人', '动漫', '三维', '风格化'];

/** 各类别"填入框架"模板（结构化填空，帮用户想全要素）。 */
export const FRAME_TEMPLATES: Record<SubjectCategory, string> = {
  character: '身份：\n年龄段：\n体型/身高：\n服装：\n发型与五官：\n气质/神态：',
  scene: '环境/地点：\n时间与天气：\n氛围/光线：\n关键陈设：\n色调：',
  prop: '名称/类别：\n材质：\n颜色：\n尺寸/比例：\n细节特征/磨损：',
};
